// @ts-nocheck
import React, { useContext, useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import GlobalStyles from "@mui/material/GlobalStyles";
import { Grid, Box, Chip, Typography } from "@mui/material";
import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
} from "@mui/x-data-grid-pro";
import { StyledMainContainer, StyledDataGrid, HiddenColumnsContainer } from "./Main.styles";
import { GlobalContext } from "context/Provider";
import API from "api";
import Session from "helpers/session";
import { useTranslation } from "react-i18next";
import { host, socket_url } from "../../api/kore";
import io from "socket.io-client";
import {
  loadMembersContext,
  loadConfigurationsContext,
  loadNotificationsContext,
  createColumns,
  formatRows,
  clientSettings,
  defaultSettings
} from "./Main.utils";
import { MEMBER_DISPATCHES } from "context/reducers/memberReducer";
import { COORDINATOR, ENCOUNTER, DEVELOPMENT } from "helpers/appConstants";
import { saveActiveEncounter } from "helpers/variables";

export default function Main() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    member,
    settings,
    memberDispatch,
    notificationsDispatch,
    encounterDispatch,
    encounterTimerDispatch,
    settingsDispatch,
    encounterTimer
  } = useContext(GlobalContext);
  const [loading, setLoading] = useState<boolean>(true);
  const [displayRows, setDisplayRows] = useState([]);
  const currentEncounter = window.sessionStorage.getItem(ENCOUNTER)
    ? JSON.parse(
        Session.decrypt(window.sessionStorage.getItem(ENCOUNTER) || "")
      )
    : "";
  const gridSettings = settings?.coordinator?.data?.system?.settings;

  const loadEncounter = async () => {
    const encounters = await API.kore.encounters.getList(
      Session.crackCookieToken(COORDINATOR)
    );
    encounterDispatch({ type: "UPDATE_LIST", payload: encounters });
  };

  // Serialize loadContext: it runs on mount AND on every socket "data-updates"
  // event, so calls can overlap. Without a guard, two concurrent runs each call
  // setDisplayRows and dispatch member data, and the slower request wins
  // regardless of which started last -> stale grid (out-of-order resolution).
  const loadInProgress = useRef(false);
  const loadQueued = useRef(false);

  const loadContext = async () => {
    // A load is already running; remember that another was requested so we
    // refresh once more on completion -- never drop the latest update.
    if (loadInProgress.current) {
      loadQueued.current = true;
      return;
    }
    loadInProgress.current = true;
    try {
      const memberData = await loadMembersContext(memberDispatch);
      await loadNotificationsContext(notificationsDispatch);
      setDisplayRows(
        await formatRows(memberData, memberDispatch, member.recents)
      );
    } catch (e) {
      console.log("error: ", e);
    } finally {
      // Always clear the loading state so a failed member/notification load
      // surfaces an empty grid instead of an indefinite spinner.
      setLoading(false);
      loadInProgress.current = false;
      if (loadQueued.current) {
        loadQueued.current = false;
        loadContext();
      }
    }
  };

  const goToReadings = (type: string, memberId: string, name: string) => {
    memberDispatch({
      type: MEMBER_DISPATCHES.changeTargetMember,
      payload: { name, memberId }
    });
    memberDispatch({ type: MEMBER_DISPATCHES.updateSeconds, payload: 0 });
    memberDispatch({ type: MEMBER_DISPATCHES.updateStart, payload: moment() });
    Session.initiateSessionEncounter(memberId, name);
    return navigate(
      `/patient/history/${type.toLowerCase().replace(" ", "")}/list/${memberId}`
    );
  };

  const goToMemberPage = (cell: any) => {
    memberDispatch({
      type: MEMBER_DISPATCHES.changeTargetMember,
      payload: { name: cell.row.member.name, id: cell.row.id }
    });
    memberDispatch({ type: MEMBER_DISPATCHES.updateSeconds, payload: 0 });
    memberDispatch({ type: MEMBER_DISPATCHES.updateStart, payload: moment() });
    Session.initiateSessionEncounter(cell.row.id, cell.row.member.username);
    navigate("/patient/vitals/" + cell.row.id);
  };

  const handleVisibilityChange = (model: Record<string, boolean>) => {
    if (!gridSettings) return;

    settingsDispatch({
      type: "UPDATE_COORDINATOR_SETTINGS",
      payload: {
        system: {
          ...settings.coordinator.data.system,
          settings: {
            ...gridSettings,
            columnVisibility: {
              ...gridSettings.columnVisibility,
              ...model
            }
          }
        }
      }
    });
  };

  const handleDensityChange = (
    newDensity: "compact" | "standard" | "comfortable"
  ) => {
    if (!gridSettings) return;

    settingsDispatch({
      type: "UPDATE_COORDINATOR_SETTINGS",
      payload: {
        system: {
          ...settings.coordinator.data.system,
          settings: {
            ...gridSettings,
            density: newDensity
          }
        }
      }
    });
  };

  const handleOrderChange = (params) => {
    if (!gridSettings?.columnOrder) return;

    const newOrder = [...gridSettings.columnOrder];
    const [movedColumn] = newOrder.splice(params.oldIndex, 1);
    newOrder.splice(params.targetIndex, 0, movedColumn);

    settingsDispatch({
      type: "UPDATE_COORDINATOR_SETTINGS",
      payload: {
        system: {
          ...settings.coordinator.data.system,
          settings: {
            ...gridSettings,
            columnOrder: newOrder
          }
        }
      }
    });
  };

  const columns = useMemo(
    () => createColumns(t,
      goToReadings,
      goToMemberPage,
      settings?.default?.data?.system?.settings
    ),
    [t, goToReadings, goToMemberPage]
  );

  const compareColumns = (
    columns: GridColDef[],
    settings?: {
      columnVisibility?: Record<string, boolean>;
      columnOrder?: string[];
      density?: "compact" | "standard" | "comfortable";
    }
  ) => {
    const columnFields = columns.map(col => col.field);

    const existingVisibility = settings?.columnVisibility || {};
    const existingOrder = settings?.columnOrder || [];

    const currentVisibility: Record<string, boolean> = {};

    columnFields.forEach(field => {
      currentVisibility[field] =
        existingVisibility[field] ?? true;
    });

    const filteredOrder = existingOrder.filter(field =>
      columnFields.includes(field)
    );

    const missingFields = columnFields.filter(
      field => !filteredOrder.includes(field)
    );

    const reconciledOrder = [...filteredOrder, ...missingFields];

    return {
      columnVisibility: currentVisibility,
      columnOrder: reconciledOrder,
      density: settings?.density ?? "standard"
    };
  };

  const finalGridSettings = useMemo(() => {
    if (!settings?.coordinator?.data?.system?.settings) return undefined;

    return compareColumns(
      columns,
      settings.coordinator.data.system.settings
    );
  }, [columns, settings]);

  const orderedColumns = useMemo(() => {
    if (!finalGridSettings?.columnOrder) return columns;

    return finalGridSettings.columnOrder
      .map(field => columns.find(col => col.field === field))
      .filter(Boolean);
  }, [columns, finalGridSettings?.columnOrder]);

  const hiddenColumns = orderedColumns.filter(
    col => finalGridSettings?.columnVisibility?.[col.field] === false
  );
  const hasHiddenColumns = hiddenColumns.length > 0;

  useEffect(() => {
    saveActiveEncounter(member, encounterTimerDispatch, settingsDispatch, encounterTimer?.current?.duration || 0);
    loadContext().catch((e) => console.log("error: ", e));
    loadEncounter();
    let socket: any;
    if (process.env.REACT_APP_NODE_ENV !== DEVELOPMENT) {
      socket = io(socket_url);
      socket.on(`${host[0]}-data-updates`, () => {
        loadContext(Session.crackCookieUserOrg(COORDINATOR));
        loadEncounter();
      });
    }

    API.kore.settings
      .getSingle("?entity=client", Session.crackCookieToken("coordinator"), Session.crackCookieUserId(COORDINATOR))
      .then((response: any) => {
        settingsDispatch({
          type: "CURRENT_COORDINATOR_SETTINGS",
          payload: response.data
        });
      })
      .catch((err: any) => {
        if (err?.response?.status === 404) {
          return API.kore.settings.create(
            clientSettings(Session.crackCookieUserId(COORDINATOR)),
            Session.crackCookieToken("coordinator")
          )
            .then((createResponse: any) => {
              settingsDispatch({
                type: "CURRENT_COORDINATOR_SETTINGS",
                payload: createResponse.data
              });
            });
        } else {
          console.log("Error loading client settings");
        }
      });
    API.kore.settings
      .getSingle("?entity=default", Session.crackCookieToken("coordinator"))
      .then((response: any) => {
        const data = response.data;

        if (!data.items || data.items.length === 0) {
          return API.kore.settings
            .create(defaultSettings(), Session.crackCookieToken("coordinator"))
            .then((createResponse: any) => {
              settingsDispatch({
                type: "CURRENT_DEFAULT_SETTINGS",
                payload: createResponse.data
              });
            });
        }

        settingsDispatch({
          type: "CURRENT_DEFAULT_SETTINGS",
          payload: data.items[0]
        });
      })
      .catch((err: any) => console.log("Error loading default settings:", err));

    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    API.kore.settings
      .update("?entity=client", settings?.coordinator?.data, Session.crackCookieToken("coordinator"), Session.crackCookieUserId(COORDINATOR))
      .catch((err: any) => console.log("Error updating client settings"));
  }, [settings]);

  useEffect(() => {
    if (!finalGridSettings || !gridSettings) return;

    const hasChanged =
      JSON.stringify(finalGridSettings) !== JSON.stringify(gridSettings);

    if (hasChanged) {
      settingsDispatch({
        type: "UPDATE_COORDINATOR_SETTINGS",
        payload: {
          system: {
            ...settings.coordinator.data.system,
            settings: finalGridSettings
          }
        }
      });
    }
  }, [finalGridSettings]);


  return (
    <Grid container>
      <Grid item xs={0.1} />
      <Grid item xs={11.9}>
        <StyledMainContainer>
          {hasHiddenColumns && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 1 }}>
              <Typography variant="body2">
                {hiddenColumns.length} column{hiddenColumns.length > 1 ? "s" : ""} hidden
              </Typography>
              <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                {hiddenColumns.map(col => (
                  <Chip
                    key={col.field}
                    label={col.headerName}
                    onDelete={() =>
                      handleVisibilityChange({
                        ...finalGridSettings.columnVisibility,
                        [col.field]: true
                      })
                    }
                  />
                ))}
              </Box>
            </Box>
            )}
          <GlobalStyles
            styles={{ ".MuiDataGrid-panel": { top: "-120px !important" } }}
          />
          <StyledDataGrid
            rows={displayRows}
            getRowHeight={() => 'auto'}
            columns={orderedColumns}
            density={gridSettings?.density ?? "standard"}
            slots={{
              toolbar: () => (
                <GridToolbarContainer
                  style={{ justifyContent: "flex-end" }}
                >
                  <GridToolbarColumnsButton />
                  <GridToolbarFilterButton />
                  <GridToolbarDensitySelector />
                </GridToolbarContainer>
              )
            }}
            slotProps={{
              panel: {
                sx: {
                  top: 100
                }
              }
            }}
            initialState={{
              sorting: {
                sortModel: [{ field: "reviewed", sort: "asc" }]
              }
            }}
            sx={{
              '& .MuiDataGrid-cell': {
                whiteSpace: 'normal',
                wordWrap: 'break-word',
                lineHeight: 'normal',
              },
            }}
            rowsPerPageOptions={[5]}
            disableSelectionOnClick
            onCellClick={goToMemberPage}
            loading={loading}
            getRowClassName={() => `super-app-theme--rows`}
            onColumnOrderChange={handleOrderChange}
            columnVisibilityModel={finalGridSettings?.columnVisibility}
            onColumnVisibilityModelChange={handleVisibilityChange}
            onDensityChange={handleDensityChange}
            disableColumnPinning
          />
        </StyledMainContainer>
      </Grid>
    </Grid>
  );
}
